import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';

import { User                 } from '@shared/factories';
import { SidebarRouteExtended } from '@shared/models';
import {
  ActivityReportsService,
  AssignmentService,
  FailedErpService,
  MileageMoneyListService,
  PhotoDocumentService,
  SessionStorageService,
  SidebarStorageService,
  UserService,
  VacationRequestListService
} from '@shared/services';

@Component({
  selector: 'counters-cards',
  templateUrl: './counters-cards.component.html',
  host: { class: 'grid-container mb-auto', '[class.mobile-show]': '!isTimeTrackingDashboard()', '[class.display-none]': 'user.isCustomer || !isTimeTracking' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CountersCardsComponent implements OnInit {
  user:           User;
  activeTab:      SidebarRouteExtended;
  isTimeTracking: boolean;
  showCandidate:  boolean;
  countersCards:  SidebarRouteExtended[];

  approvedReportsCount:              number;
  openActivityReportsCount:          number;
  clarificationReportsCount:         number;
  failedExportActivityReportsCount:  number;
  expiringSoonAssignmentsCount:      number;
  activePhotoDocumentsCount:         number;
  unconfirmedEBSCount:               number;
  approvedVacationRequestsCount:     number;
  awaitingVacationRequestsCount:     number;
  rejectedVacationRequestsCount:     number;
  failedExportVacationRequestsCount: number;
  awaitingMileageMoneyReportsCount:  number;
  constructor(
    private router:                     Router,
    private sidebarStorageService:      SidebarStorageService,
    private activityReportsService:     ActivityReportsService,
    private vacationRequestListService: VacationRequestListService,
    private photoDocumentService:       PhotoDocumentService,
    private assignmentService:          AssignmentService,
    private mileageMoneyListService:    MileageMoneyListService,
    private failedErpService:           FailedErpService,
    private sessionStorageService:      SessionStorageService,
    private userService:                UserService
  ) { }

  ngOnInit(): void {
    this.userService.currentUser.subscribe(user => this.user = user);
    this.sessionStorageService.activeTab.subscribe(tab => this.activeTab = tab);

    this.sessionStorageService.activeApp.subscribe(() => {
      this.isTimeTracking = this.sessionStorageService.isTimeTracking;
      this.reloadCards();
    });

    this.sidebarStorageService.approvedReportsCount.             subscribe(items => this.approvedReportsCount              = items);
    this.sidebarStorageService.openActivityReportsCount.         subscribe(items => this.openActivityReportsCount          = items);
    this.sidebarStorageService.clarificationReportsCount.        subscribe(items => this.clarificationReportsCount         = items);
    this.sidebarStorageService.failedExportActivityReportsCount. subscribe(items => this.failedExportActivityReportsCount  = items);
    this.sidebarStorageService.activePhotoDocumentsCount.        subscribe(items => this.activePhotoDocumentsCount         = items);
    this.sidebarStorageService.unconfirmedEBSCount.              subscribe(items => this.unconfirmedEBSCount               = items);
    this.sidebarStorageService.expiringSoonAssignmentsCount.     subscribe(items => this.expiringSoonAssignmentsCount      = items);
    this.sidebarStorageService.approvedVacationRequestsCount.    subscribe(items => this.approvedVacationRequestsCount     = items);
    this.sidebarStorageService.awaitingVacationRequestsCount.    subscribe(items => this.awaitingVacationRequestsCount     = items);
    this.sidebarStorageService.rejectedVacationRequestsCount.    subscribe(items => this.rejectedVacationRequestsCount     = items);
    this.sidebarStorageService.failedExportVacationRequestsCount.subscribe(items => this.failedExportVacationRequestsCount = items);
    this.sidebarStorageService.awaitingMileageMoneyReportsCount. subscribe(items => this.awaitingMileageMoneyReportsCount  = items);
  }

  private reloadCards(): void {
    if (this.isTimeTracking) {
      let navItemsMapped = this.sidebarStorageService.navItemsMapped.filter(i => !i.bottom);
      this.countersCards = this.collectCards(navItemsMapped);
    } else this.countersCards = null;
  }

  mapCardCount(counter: string): number {
    switch (counter) {
      case 'dashboard':     { return this.approvedReportsCount + this.openActivityReportsCount + this.clarificationReportsCount;               };
      case 'vr-dashboard':  { return this.approvedVacationRequestsCount+this.awaitingVacationRequestsCount+this.rejectedVacationRequestsCount; };
      case 'ar-failed-erp': { return this.failedExportActivityReportsCount+this.failedExportVacationRequestsCount;                             };
      case 'ebs-list':      { return this.expiringSoonAssignmentsCount || this.unconfirmedEBSCount || 0;                                       };
      case 'photo-doc':     { return this.activePhotoDocumentsCount;                                                                           };
      case 'mm-list':       { return this.awaitingMileageMoneyReportsCount;                                                                    };
    };
  }
  
  mapCardTitle(counter: string): string {
    switch (counter) {
      case 'dashboard':     { return 'TN';     };
      case 'ebs-list':      { return 'EBS';    };
      case 'photo-doc':     { return  this.activePhotoDocumentsCount          === 1 ? 'Fotodokument'         : 'Fotodokumente';         };
      case 'mm-list':       { return  this.awaitingMileageMoneyReportsCount   === 1 ? 'Kilometergeldbericht' : 'Kilometergeldberichte'; };
      case 'ar-failed-erp': { return (this.failedExportActivityReportsCount +
                                      this.failedExportVacationRequestsCount) === 1 ? 'Fehlübertragung'      : 'Fehlübertragungen';     };
      case 'vr-dashboard':  { return  this.approvedVacationRequestsCount +
                                      this.awaitingVacationRequestsCount +
                                      this.rejectedVacationRequestsCount      === 1 ? 'Urlaubsantrag'        : 'Urlaubsanträge';        };
    };
  }

  mapCardSubtitle(counter: string): string {
    switch (counter) {
      case 'dashboard':
      case 'vr-dashboard':  { return 'in Prüfung'; };
      case 'ebs-list':
      case 'photo-doc':
      case 'mm-list':       { return 'ungelesen';  };
      case 'ar-failed-erp': { return `${this.failedExportActivityReportsCount} TN - ${this.failedExportVacationRequestsCount} Urlaub`; };
    };
  }

  changeDashboard(card: SidebarRouteExtended): void {
    if      (this.isActive(card)) this.reloadList(card);
    else {
      this.router.navigateByUrl(`/time-tracking/${card?.path}`);
      this.sessionStorageService.changeAndResetActiveTab(card.identifier);
      if (this.checkByPath(card)) this.reloadList(card);
    }
  }

  private reloadList(card: SidebarRouteExtended): void {
    switch (card.identifier) {
      case 'dashboard':     { this.activityReportsService.forceReload();     break; }
      case 'vr-dashboard':  { this.vacationRequestListService.forceReload(); break; }
      case 'ar-failed-erp': { this.failedErpService.forceReload();           break; }
      case 'vr-failed-erp': { this.failedErpService.forceReload();           break; }
      case 'photo-doc':     { this.photoDocumentService.forceReload();       break; }
      case 'ebs-list':      { this.assignmentService.forceReload();          break; }
      case 'mm-list':       { this.mileageMoneyListService.forceReload();    break; }
      default:              { break; }
    }
  }

  private collectCards(navItems: SidebarRouteExtended[]): SidebarRouteExtended[] {
    return [
      { identifier: 'dashboard'                         },
      { identifier: 'vr-dashboard'                      },
      { identifier: 'ar-failed-erp', internalOnly: true },
      { identifier: 'ebs-list'                          },
      { identifier: 'photo-doc',     internalOnly: true },
      { identifier: 'mm-list',       internalOnly: true },
    ].filter(i => i.internalOnly ? this.user.isInternal : true)
     .map(i => navItems.find(n => n.identifier === i.identifier));
  }

  isActive(card: SidebarRouteExtended): boolean {
    if (this.activeTab.identifier.includes('failed-erp') && card.identifier.includes('failed-erp')) return true;
    return this.activeTab.identifier === card.identifier && this.isListView();
  }

  isTimeTrackingDashboard(): boolean {
    //if showCandidate is true, that means user.isCustomer && user.isAuthorizedTemptonConnect && candidates.length > 0
    return this.isTimeTracking && this.checkByPath(this.activeTab);
  }

  private checkByPath(navItem: SidebarRouteExtended): boolean {
    return navItem?.path && this.router.url.split('?')[0] === `/time-tracking/${navItem.path.split('?')[0]}`;
  }

  private isListView(): boolean {
    let navItemsMapped = this.sidebarStorageService.navItemsMapped.filter(i => !i.bottom);
    let sidebarItem    = navItemsMapped.find(navItem => this.checkByPath(navItem));
    return !!sidebarItem;
  }

}
